/**
 * TradeChecklist — Pre-flight readiness indicator for swing candidates.
 *
 * Computes 6 actionable checks on a scan result:
 *   1. Score ≥ 70
 *   2. Vol Ratio ≥ 1.5× (breakout / signal-day participation)
 *   3. Within 10% of 52-week high
 *   4. EMA aligned (close > EMA20 > EMA50)
 *   5. Sector budget not maxed (needs sectorDeployment + sectorCap props)
 *   6. Setup tag present (BREAKOUT / RETEST / RS_LEADER)
 *
 * Two render modes:
 *   - <TradeChecklist compact />        — Ready badge for scanner column
 *   - <TradeChecklist />                — Full labelled list for breakdown panel
 *
 * Always-passing implicit checks (auto-enforced by backend):
 *   - Risk per trade ≤ risk_pct (sizing calculator)
 *   - SL placed below recent swing low (compute_levels)
 *
 * Not-checkable here (operator must verify manually):
 *   - No earnings in next 5 trading days — yfinance's calendar is unreliable
 */
import React from 'react';

const PASS = '#4CAF50';
const FAIL = '#f44336';
const SKIP = '#666';

export function computeChecks(result, ctx = {}) {
  const {
    sectorDeployment = null,    // { "IT": 22.5, "FMCG": 8.0, ... } or null
    sectorCapPct     = 30,
    score_threshold  = 70,
    vol_threshold    = 1.5,
    high52_threshold = -10,
  } = ctx;

  const score = result.composite_score || 0;
  const vol   = result.volume_ratio;
  const w52   = result.week52_high_pct;
  const close = result.close_price;
  const ema20 = result.ema20;
  const ema50 = result.ema50;
  const tags  = (result.setup_tags || result.setup_tag || '').toUpperCase();

  const emaAligned = !!(close && ema20 && ema50 && close > ema20 && ema20 > ema50);
  const hasGoodTag = ['BREAKOUT', 'RETEST', 'RS_LEADER'].some(t => tags.includes(t));

  const checks = [
    {
      key: 'score',
      label: `Score ≥ ${score_threshold}`,
      pass: score >= score_threshold,
      detail: `${Math.round(score)}`,
    },
    {
      key: 'volume',
      label: `Vol Ratio ≥ ${vol_threshold.toFixed(1)}×`,
      pass: vol != null && vol >= vol_threshold,
      detail: vol != null ? `${parseFloat(vol).toFixed(2)}×` : '—',
    },
    {
      key: 'high52',
      label: `Within ${Math.abs(high52_threshold)}% of 52w high`,
      pass: w52 != null && w52 >= high52_threshold,
      detail: w52 != null ? `${parseFloat(w52).toFixed(1)}%` : '—',
    },
    {
      key: 'ema',
      label: 'EMA aligned (Close > 20 > 50)',
      pass: emaAligned,
      detail: emaAligned ? 'Aligned' : 'Mixed',
    },
    {
      key: 'tag',
      label: 'Has quality setup tag',
      pass: hasGoodTag,
      detail: hasGoodTag ? tags.split(',')[0] : 'Generic',
    },
  ];

  // Sector budget check — only when we have portfolio context
  if (sectorDeployment != null && result.sector) {
    const used = sectorDeployment[result.sector] || 0;
    const headroom = sectorCapPct - used;
    checks.push({
      key: 'sector',
      label: `Sector room ≥ 5%`,
      pass: headroom >= 5,
      detail: `${used.toFixed(1)}% used (${headroom.toFixed(1)}% free)`,
    });
  }

  const passCount  = checks.filter(c => c.pass).length;
  const total      = checks.length;
  const readiness  = passCount === total ? 'READY'
                   : passCount >= total - 1 ? 'NEAR'
                   : 'NOT_READY';

  return { checks, passCount, total, readiness };
}


function ReadinessBadge({ readiness, passCount, total }) {
  const styles = {
    READY:     { bg: '#0a2010', color: '#4CAF50', border: '#2d6a2d', label: 'READY' },
    NEAR:      { bg: '#3a2a00', color: '#FFC107', border: '#7a5a00', label: 'NEAR' },
    NOT_READY: { bg: '#2a1010', color: '#f44',   border: '#5a2020', label: 'CAUTION' },
  };
  const s = styles[readiness];
  return (
    <span style={{
      padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      letterSpacing: '0.05em',
    }}>
      {s.label} {passCount}/{total}
    </span>
  );
}


export default function TradeChecklist({ result, ctx, compact = false }) {
  const { checks, passCount, total, readiness } = computeChecks(result, ctx);

  if (compact) {
    // Compact: badge + tiny dot row, ideal for table column
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
        <ReadinessBadge readiness={readiness} passCount={passCount} total={total} />
        <div style={{ display: 'flex', gap: 2 }}>
          {checks.map(c => (
            <span
              key={c.key}
              title={`${c.label}: ${c.pass ? '✓ Pass' : '✗ Fail'} — ${c.detail}`}
              style={{
                width: 8, height: 8, borderRadius: 99,
                background: c.pass ? PASS : FAIL,
                opacity: c.pass ? 1 : 0.7,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  // Full view: labelled rows
  return (
    <div style={{ background: '#0f0f0f', border: '1px solid #2a2a2a', borderRadius: 6, padding: 10 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #222',
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Pre-Trade Checklist
        </span>
        <ReadinessBadge readiness={readiness} passCount={passCount} total={total} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '4px 12px' }}>
        {checks.map(c => (
          <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 16, height: 16, borderRadius: 99, fontSize: 10, fontWeight: 700,
              background: c.pass ? '#0a2010' : '#2a1010',
              color: c.pass ? PASS : FAIL,
              border: `1px solid ${c.pass ? '#2d6a2d' : '#5a2020'}`,
            }}>
              {c.pass ? '✓' : '✗'}
            </span>
            <span style={{ color: c.pass ? '#bbb' : '#888' }}>{c.label}</span>
            <span style={{ marginLeft: 'auto', color: c.pass ? PASS : FAIL, fontFamily: 'monospace', fontSize: 11 }}>
              {c.detail}
            </span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid #222', fontSize: 10, color: SKIP }}>
        ⓘ Auto-enforced by system: risk-per-trade ≤ {ctx?.riskPct || 1.5}% · SL below recent swing low
        <br />
        ⓘ <strong style={{ color: '#FF9800' }}>Manual check:</strong> verify no earnings announcement within 5 trading days
      </div>
    </div>
  );
}
